import { FC } from "react";
import { Link } from "react-router-dom";
import { ProductDataType } from "data/types";
import Pagination from "shared/Pagination/Pagination";
import ProductCard from "components/ProductCard/ProductCard";
import ProductsCategorySidebar, {
  CategoryGroup,
} from "./ProductsCategorySidebar";
import {
  CubeIcon,
  ShieldCheckIcon,
  TruckIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";

export interface SectionGridFilterCardProps {
  className?: string;
  data?: ProductDataType[];
  categories?: CategoryGroup[];
  currentPage?: number;
  totalPages?: number;
  totalCount?: number;
  activeSubcategoryName?: string;
  searchQuery?: string | null;
  onPageChange?: (page: number) => void;
  isLoading?: boolean;
}

const trustItems = [
  { icon: ShieldCheckIcon, label: "UK Engineered Quality" },
  { icon: TruckIcon, label: "UAE Stock & Fast Delivery" },
  { icon: CubeIcon, label: "2,000+ Profile Variants" },
  { icon: DocumentTextIcon, label: "Technical Spec Sheets" },
];

const SectionGridFilterCard: FC<SectionGridFilterCardProps> = ({
  className = "",
  data = [],
  categories = [],
  currentPage = 1,
  totalPages = 1,
  totalCount = 0,
  activeSubcategoryName,
  searchQuery,
  onPageChange,
  isLoading = false,
}) => {
  const showingFrom = totalCount === 0 ? 0 : (currentPage - 1) * 12 + 1;
  const showingTo = Math.min(currentPage * 12, totalCount);

  return (
    <div
      className={`nc-SectionGridFilterCard ${className}`}
      data-nc-id="SectionGridFilterCard"
    >
      {/* Trust strip — common on industrial B2B catalogs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8 sm:mb-10">
        {trustItems.map((item) => (
          <div
            key={item.label}
            className="flex items-center gap-3 px-4 py-3 rounded-xl border border-neutral-200/80 dark:border-neutral-700 bg-white dark:bg-neutral-900"
          >
            <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-primary-50 dark:bg-primary-900/30 flex items-center justify-center">
              <item.icon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </span>
            <span className="text-xs sm:text-sm font-medium text-neutral-700 dark:text-neutral-300 leading-snug">
              {item.label}
            </span>
          </div>
        ))}
      </div>

      <div className="flex flex-col lg:flex-row gap-8 lg:gap-10">
        {/* Sidebar — desktop */}
        {categories.length > 0 && (
          <ProductsCategorySidebar
            categories={categories}
            className="hidden lg:block w-full lg:w-[260px] xl:w-[280px] shrink-0 lg:sticky lg:top-24 lg:self-start"
          />
        )}

        <div className="flex-1 min-w-0">
          {/* Mobile category chips */}
          {categories.length > 0 && (
            <div className="lg:hidden mb-6 -mx-1">
              <div className="flex gap-2 overflow-x-auto pb-2 px-1 bcr-scrollbar-hide">
                <Link
                  to="/products"
                  className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                    !activeSubcategoryName && !searchQuery
                      ? "bg-primary-600 text-white border-primary-600"
                      : "bg-white dark:bg-neutral-900 border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300"
                  }`}
                >
                  All
                </Link>
                {categories.flatMap((g) =>
                  g.items.map((sub) => (
                    <Link
                      key={sub.id}
                      to={sub.href}
                      className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium border transition-colors whitespace-nowrap ${
                        activeSubcategoryName === sub.name
                          ? "bg-primary-600 text-white border-primary-600"
                          : "bg-white dark:bg-neutral-900 border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300"
                      }`}
                    >
                      {sub.name}
                    </Link>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Toolbar */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6 sm:mb-8 pb-5 border-b border-neutral-200 dark:border-neutral-700">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-white">
                {searchQuery
                  ? `Results for "${searchQuery}"`
                  : activeSubcategoryName || "All Products"}
              </h2>
              {!isLoading && (
                <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                  {totalCount > 0
                    ? `Showing ${showingFrom}–${showingTo} of ${totalCount} products`
                    : "No products found"}
                </p>
              )}
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-semibold rounded-full bcr-btn-primary !py-2.5 shrink-0"
            >
              Request a Quote
            </Link>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 sm:gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="animate-pulse rounded-2xl border border-neutral-200 dark:border-neutral-700 overflow-hidden bg-white dark:bg-neutral-900">
                  <div className="aspect-[4/3] bg-neutral-200 dark:bg-neutral-700" />
                  <div className="p-5 space-y-3">
                    <div className="h-4 w-3/4 rounded bg-neutral-200 dark:bg-neutral-700" />
                    <div className="h-3 w-1/2 rounded bg-neutral-200 dark:bg-neutral-700" />
                    <div className="h-8 w-full rounded-lg bg-neutral-200 dark:bg-neutral-700" />
                  </div>
                </div>
              ))}
            </div>
          ) : data.length === 0 ? (
            <div className="text-center py-16 sm:py-20 px-6 rounded-2xl border border-dashed border-neutral-300 dark:border-neutral-600 bg-neutral-50 dark:bg-neutral-800/40">
              <CubeIcon className="w-12 h-12 mx-auto text-neutral-400" />
              <h3 className="mt-4 text-lg font-semibold text-neutral-900 dark:text-white">
                No products found
              </h3>
              <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400 max-w-md mx-auto">
                Try browsing a different category or contact our team for
                custom profile solutions.
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <Link
                  to="/products"
                  className="px-5 py-2.5 text-sm font-semibold rounded-full border border-neutral-300 dark:border-neutral-600 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                >
                  View all products
                </Link>
                <Link to="/contact" className="bcr-btn-primary text-sm !px-5 !py-2.5">
                  Contact us
                </Link>
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 sm:gap-6">
                {data.map((product) => (
                  <ProductCard key={product.SKU} data={product} />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex mt-12 sm:mt-16 justify-center items-center">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={onPageChange}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SectionGridFilterCard;
